<template>
  <Dialog
    v-model:visible="visible"
    :modal="props.modal"
    :closable="props.closable"
    :header="props.header"
    :dismissableMask="props.dismissableMask"
    :class="['dialog-wrapper', smallerTitle && 'smaller-title']"
    :aria-labelledby="`Confirmation to ${props.header}`"
    :aria-expanded="visible"
    :close-on-escape="true"
  >
    <slot></slot>
    <div v-focustrap aria-modal="true" ref="confirmation-dialog-content-ref" class="modal-buttons">
      <Button
        @click="cancel"
        @keydown="handleCancel"
        class="modal-button light-grey"
        :label="props.buttonTextCancel"
        :aria-label="props.buttonTextCancel"
        autofocus
      ></Button>
      <Button
        @click="confirm"
        @keydown="handleConfirm"
        :class="{
          'modal-button': true,
          'red': isRedButton,
          'light-purple': !isRedButton && !isVibrantPurple,
          'vibrant-purple': isVibrantPurple
        }"
        :label="props.buttonTextConfirm"
        :aria-label="props.buttonTextConfirm"
      >
      </Button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">

import { ref } from "vue";
let visible = ref(false);
const props = defineProps<{
  modal: boolean;
  closable?: boolean;
  dismissableMask?: boolean;
  header: string;
  buttonTextCancel: string;
  buttonTextConfirm: string;
  isRedButton?: boolean;
  smallerTitle: boolean;
  isVibrantPurple?: boolean;
}>();

/* eslint-disable-next-line no-unused-vars */
let resolvePromise: ((_value: boolean) => void) | null = null;

const show = (): Promise<boolean> => {
  visible.value = true;

  return new Promise((resolve) => {
    resolvePromise = resolve;
  });
};

const confirm = () => {
  visible.value = false;
  if (resolvePromise) {
    resolvePromise(true);
  }
};

const cancel = () => {
  visible.value = false;
  if (resolvePromise) {
    resolvePromise(false);
  }
};

const handleCancel = (event: KeyboardEvent) => {
  if (event.key === "Enter") {
    event.preventDefault();
    (event.target as HTMLElement).blur();
    cancel();
  }
}

const handleConfirm = (event: KeyboardEvent) => {
  if (event.key === "Enter") {
    event.preventDefault();
    (event.target as HTMLElement).blur();
    confirm();
  }
}

defineExpose({
  show,
});
</script>

<style lang="scss">
.dialog-wrapper {
  width: 44rem;
  .p-dialog-header {
    color: white;
    border-top-right-radius: 1.4rem;
    border-top-left-radius: 1.4rem;
    background-color: map-get($component-colours, background-primary);
    padding: 3rem;
    display: inline-flex;
    justify-content: center;

    .p-dialog-title {
      margin-top: 2.4rem;
      @include h2-accent;
    }

    .p-dialog-header-actions {
      top: 1.6rem;
      right: 1.6rem;
      position: absolute;
      color: #bababa;
      .pi {
        font-size: 1.8rem;
      }
    }
  }

  .p-dialog-content {
    color: white;
    border-bottom-left-radius: 1.4rem;
    border-bottom-right-radius: 1.4rem;
    background-color: map-get($component-colours, background-primary);
    text-align: center;
    padding: 0rem 4rem 4rem 4rem;
    .p-button-label, p {
      @include section-sub-header;
      margin: 0;
    }
    p {
      color: map-get($colours, translucent-white-80);
    }

    .modal-buttons {
      display: flex;
      margin-top: 4rem;
      font-weight: 500;

      .modal-button {
        height: 5.6rem;
        width: 16.8rem;
        border-radius: 0.8rem;
        @include section-sub-header;
        &.light-grey {
          background-color: map-get($colours, grey-2);
          color: #474747;
        }

        &.red {
          margin-left: 2.4rem;
          background-color: map-get($colours, carmine-red);
          color: #fff;
          border: none;
        }

        &.light-purple {
          margin-left: 2.4rem;
          background-color: map-get($colours, accent-purple);
          color: #fff;
        }

        &.vibrant-purple {
          margin-left: 2.4rem;
          background-color: map-get($colours, vibrant-purple);
          color: map-get($colours, grey-7);
          border: none;
        }
      }
    }
  }

  .p-dialog-header .p-dialog-title {
    font-family: $font-accent;
    font-size: 2.4rem;
    font-weight: 500;
    line-height: 3.2rem;
    letter-spacing: .018px;
    margin: 0;
  }
}

.smaller-title {
  .p-dialog-header .p-dialog-title {
    @include h3-accent;
  }
  .p-dialog-content p {
    @include callout;
    margin: 0;
  }
}
</style>

